import {
    Color3,
    Mesh,
    MeshBuilder,
    Quaternion,
    type TrailMesh,
    TransformNode,
    Vector3,
    type ParticleSystem,
    type Scene,
    type ShaderMaterial,
    type StandardMaterial,
} from '@babylonjs/core';
import { registerNpcMotionShaders } from './npc-motion-shaders';
import { createDebrisBurst, createDustBurst, createDustStream, createNpcMotionMaterial } from './npc-motion-particles';
import { createSpriteMaterial, PARTICLE_SPRITE_FAMILIES } from './particle-sprites';
import { reconcileKeyedEffects } from './keyed-effects';
import { createRibbonTrail } from './ribbon-trail';

/** A charging enemy, as it winds up and as it runs down its lane. */
export interface ChargeRenderState {
    readonly id: string;
    /** Winding up at the start of the lane, or running down it. */
    readonly phase: 'windup' | 'run';
    /** Its feet, on the floor. */
    readonly feet: Vector3;
    /** Horizontal unit vector down the lane. */
    readonly direction: Vector3;
}

export interface ChargeWallImpactOptions {
    /** Where it struck the wall, on the floor at the wall's face. */
    readonly point: Vector3;
    /** Horizontal unit vector it was charging along; the debris flies back against it. */
    readonly direction: Vector3;
}

/** Where the speed streaks leave the body: shoulder height, this far either side. */
const SHOULDER_HEIGHT = 1.15;
const SHOULDER_HALF_SPAN = 0.3;
/** A streak's half-width, in meters, and how many frames of path it keeps. */
const STREAK_HALF_WIDTH = 0.045;
const STREAK_FRAMES = 12;
/** Once a charge stops, its streaks fade over this long. */
const STREAK_FADE_SECONDS = 0.3;
const SCRAPE_EMIT_RATE = 24;
const WAKE_EMIT_RATE = 90;
/** How long the wall's flash lasts. */
const IMPACT_FLASH_SECONDS = 0.25;
const IMPACT_FLASH_SIZE = 1.6;
/** A ragged impact burst (one of the scorch sprites), warm white, a little over full so it reads hot. */
const IMPACT_FLASH_COLOR = new Color3(1.3, 1.05, 0.8);
/** The flash goes off at about chest height. */
const IMPACT_FLASH_HEIGHT = 0.8;
/** Time left after the last piece for particles to die out before an effect is cleaned up. */
const LINGER_SECONDS = 1;

interface Streak {
    readonly generator: TransformNode;
    readonly trail: TrailMesh;
    readonly material: ShaderMaterial;
    /** Which side of the body: -1 left, 1 right. */
    readonly side: number;
}

interface ChargeEntry {
    readonly id: string;
    readonly root: TransformNode;
    readonly dust: ParticleSystem;
    streaks: Streak[] | null;
    /** Set once its state is gone: seconds it has been ending for. */
    endingFor: number | null;
}

interface ImpactBurst {
    readonly root: TransformNode;
    readonly flash: Mesh;
    readonly flashMaterial: StandardMaterial;
    readonly particles: readonly ParticleSystem[];
    elapsed: number;
}

/**
 * Draws an enemy's charge (docs/gameplay/combat-vfx.md, "Charge"): dust
 * scraping back from its feet as it winds up (its lane is a
 * `GroundLineSystem` line, filling with the windup), then a dust wake behind it and speed streaks off its shoulders
 * while it runs. Hitting a wall (`wallImpact`) flashes and throws debris
 * where it struck.
 *
 * Charges are set every frame (`setCharges`); a charge whose state is gone
 * has stopped, and its wake and streaks fade out where they are. Dust and
 * debris are Babylon particles - client visuals need no `SeededRng`. The
 * stars a wall stun circles over its head are `StunStarsSystem`'s.
 */
export class ChargeSystem {
    private readonly charges = new Map<string, ChargeEntry>();
    private readonly impacts: ImpactBurst[] = [];
    private impactCount = 0;

    constructor(private readonly scene: Scene) {
        registerNpcMotionShaders();
    }

    /** Shows exactly these charges: new ones are built, gone ones fade out, the rest follow their enemies. */
    setCharges(states: readonly ChargeRenderState[]): void {
        reconcileKeyedEffects(this.charges, states, {
            create: (state) => this.createCharge(state),
            // One already ending starts over.
            isStale: (entry) => entry.endingFor !== null,
            apply: (entry, state) => this.applyState(entry, state),
            unseen: (entry) => {
                if (entry.endingFor === null) {
                    this.endCharge(entry);
                }
                return 'keep';
            },
            discard: (entry) => this.disposeCharge(entry),
        });
    }

    /** A flash and debris where a charge struck a wall. */
    wallImpact(options: ChargeWallImpactOptions): void {
        const id = `charge-impact-${this.impactCount++}`;
        const root = new TransformNode(id, this.scene);
        const flashSprite = { family: 'scorch', index: 1 + Math.floor(Math.random() * PARTICLE_SPRITE_FAMILIES.scorch) } as const;
        const flashMaterial = createSpriteMaterial(this.scene, `${id}-flash`, flashSprite, IMPACT_FLASH_COLOR, true);
        const flash = MeshBuilder.CreatePlane(`${id}-flash`, { size: IMPACT_FLASH_SIZE }, this.scene);
        flash.parent = root;
        flash.billboardMode = Mesh.BILLBOARDMODE_ALL;
        flash.position.set(options.point.x, options.point.y + IMPACT_FLASH_HEIGHT, options.point.z);
        flash.material = flashMaterial;
        flash.isPickable = false;
        // Turned at random, so repeated impacts differ.
        flash.rotation.z = Math.random() * Math.PI * 2;

        const back = options.direction.scale(-1);
        const debris = createDebrisBurst(this.scene, `${id}-debris`, {
            origin: options.point.add(new Vector3(0, IMPACT_FLASH_HEIGHT * 0.8, 0)),
            count: 22,
            // Thrown back off the wall, and up.
            direction1: back.scale(1.2).add(new Vector3(-0.8, 0.4, -0.8)),
            direction2: back.scale(2).add(new Vector3(0.8, 1.6, 0.8)),
            power: 2,
        });
        const dust = createDustBurst(this.scene, `${id}-dust`, { feet: options.point, radius: 0.2, count: 18, power: 1.2, size: 0.24 });
        debris.start();
        dust.start();
        this.impacts.push({ root, flash, flashMaterial, particles: [debris, dust], elapsed: 0 });
    }

    update(deltaSeconds: number): void {
        for (const [id, entry] of this.charges) {
            if (entry.endingFor === null) {
                continue;
            }
            entry.endingFor += deltaSeconds;
            const fade = Math.max(0, 1 - (entry.endingFor / STREAK_FADE_SECONDS));
            entry.streaks?.forEach((streak) => streak.material.setFloat('fade', fade));
            if (entry.endingFor >= LINGER_SECONDS) {
                this.disposeCharge(entry);
                this.charges.delete(id);
            }
        }
        for (let index = this.impacts.length - 1; index >= 0; index -= 1) {
            const impact = this.impacts[index]!;
            impact.elapsed += deltaSeconds;
            if (impact.elapsed >= LINGER_SECONDS) {
                this.disposeImpact(impact);
                this.impacts.splice(index, 1);
                continue;
            }
            const flash = Math.min(1, impact.elapsed / IMPACT_FLASH_SECONDS);
            impact.flash.setEnabled(flash < 1);
            impact.flash.scaling.setAll(0.7 + (0.5 * flash));
            impact.flashMaterial.alpha = (1 - flash) * (1 - flash);
        }
    }

    dispose(): void {
        this.charges.forEach((entry) => this.disposeCharge(entry));
        this.charges.clear();
        this.impacts.forEach((impact) => this.disposeImpact(impact));
        this.impacts.length = 0;
    }

    private createCharge(state: ChargeRenderState): ChargeEntry {
        const root = new TransformNode(`charge-${state.id}`, this.scene);
        const dust = createDustStream(this.scene, `charge-dust-${state.id}`, state.feet);
        dust.start();
        return { id: state.id, root, dust, streaks: null, endingFor: null };
    }

    private applyState(entry: ChargeEntry, state: ChargeRenderState): void {
        const emitter = entry.dust.emitter as Vector3;
        emitter.set(state.feet.x, state.feet.y + 0.1, state.feet.z);
        const back = state.direction.scale(-1);
        const lateral = new Vector3(-state.direction.z, 0, state.direction.x);
        if (state.phase === 'windup') {
            // The scrape: dust kicked back from its feet as it leans in.
            entry.dust.emitRate = SCRAPE_EMIT_RATE;
            entry.dust.direction1 = back.scale(1.5).add(lateral.scale(-0.6)).add(new Vector3(0, 0.3, 0));
            entry.dust.direction2 = back.scale(2.2).add(lateral.scale(0.6)).add(new Vector3(0, 0.8, 0));
            return;
        }
        // The wake: dust thrown up behind it as it runs.
        entry.dust.emitRate = WAKE_EMIT_RATE;
        entry.dust.direction1 = back.scale(0.4).add(lateral.scale(-0.8)).add(new Vector3(0, 0.3, 0));
        entry.dust.direction2 = back.scale(1).add(lateral.scale(0.8)).add(new Vector3(0, 0.9, 0));
        entry.streaks ??= [-1, 1].map((side) => this.createStreak(entry.id, side, state.feet, lateral));
        for (const streak of entry.streaks) {
            this.placeStreak(streak, state.feet, lateral);
        }
    }

    /**
     * A streak off one shoulder, its generator placed on the body before its
     * trail is made: a `TrailMesh` fills its whole history with where its
     * generator is as it is made, so one made at the scene's origin would
     * first draw a streak from there to the charger.
     */
    private createStreak(id: string, side: number, feet: Vector3, lateral: Vector3): Streak {
        const name = `charge-streak-${id}-${side < 0 ? 'left' : 'right'}`;
        const generator = new TransformNode(`${name}-generator`, this.scene);
        generator.rotationQuaternion = new Quaternion();
        this.placeGenerator(generator, side, feet, lateral);
        const material = createNpcMotionMaterial(this.scene, name, 'npcSpeedStreak', { uniforms: ['fade'], additive: true });
        material.setFloat('fade', 1);
        // Two sections make a flat ribbon across the generator's local x: across the body, so it lies flat and reads from above.
        const trail = createRibbonTrail(name, generator, this.scene, STREAK_HALF_WIDTH, STREAK_FRAMES, material);
        return { generator, trail, material, side };
    }

    private placeStreak(streak: Streak, feet: Vector3, lateral: Vector3): void {
        this.placeGenerator(streak.generator, streak.side, feet, lateral);
    }

    /** A streak's generator at the shoulder on `side`, lying across the body. */
    private placeGenerator(generator: TransformNode, side: number, feet: Vector3, lateral: Vector3): void {
        generator.position.set(
            feet.x + (lateral.x * SHOULDER_HALF_SPAN * side),
            feet.y + SHOULDER_HEIGHT,
            feet.z + (lateral.z * SHOULDER_HALF_SPAN * side),
        );
        if (lateral.lengthSquared() > 1e-6) {
            Quaternion.FromUnitVectorsToRef(Vector3.Right(), lateral.normalizeToNew(), generator.rotationQuaternion!);
        }
        // The trail reads the generator's world matrix before the next render.
        generator.computeWorldMatrix(true);
    }

    /** It has stopped: the dust stops coming and the streaks stop following and fade where they are. */
    private endCharge(entry: ChargeEntry): void {
        entry.endingFor = 0;
        entry.dust.stop();
        entry.streaks?.forEach((streak) => streak.trail.stop());
    }

    private disposeCharge(entry: ChargeEntry): void {
        // The particle texture is the shared glow halo, so it is kept.
        entry.dust.dispose(false);
        entry.streaks?.forEach((streak) => {
            streak.trail.dispose();
            streak.material.dispose();
            streak.generator.dispose();
        });
        entry.root.dispose();
    }

    private disposeImpact(impact: ImpactBurst): void {
        impact.particles.forEach((particles) => particles.dispose(false));
        impact.flashMaterial.dispose();
        impact.root.dispose();
    }
}
